<script setup lang="ts">
import type { ProductInformation } from "~/models/Content/ContentProducts"

const props = defineProps({
  selectedProduct: {
    type: Object as PropType<ProductInformation>,
    required: false,
  },
  productKey: {
    type: String,
    required: true,
  },
  step: {
    type: String,
    required: true,
  },
  activeStep: {
    type: String,
    required: true,
  },
})

const checkoutShow = defineModel<boolean>("checkoutShow")
const checkoutError = defineModel<boolean>("checkoutError")

const licencesQuery = useLicences()

// Ensure we have the latest licences
if (licencesQuery.isFetching.value) {
  await licencesQuery.suspense()
}

const hasLicence = computed(
  () => !!props.selectedProduct?.userHasLicence,
)

const checkout = useCheckout()
const { isAuthorized } = useAuth()

function submit() {
  checkoutShow.value = true
  checkoutError.value = false

  if (props.selectedProduct?.ean) {
    checkout
      .submit(
        { product: props.productKey, variant: props.selectedProduct.variantKey },
        props.selectedProduct.ean,
      )
      .catch(() => {
        captureException(
          `Unable to submit payment request to via checkout API to Vipps for product [${props.productKey}] with variant [${props.selectedProduct?.shortVariantLabel}]`,
        )
        checkoutError.value = true
      })
  } else {
    captureException(
      `EAN is missing in payment step of product [${props.productKey}] with variant [${props.selectedProduct?.shortVariantLabel}]`,
    )
    checkoutError.value = true
  }
}

const { data } = useEcommerceCatalogue()

const productPrice = computed(() => getProductPrice(props.selectedProduct, data))

const isPaid = computed(() => !!productPrice.value?.actual)
</script>

<template>
  <UStep v-if="selectedProduct" :is-active-step="activeStep === step">
    <!--  TODO: Handle product variant not found  -->
    <template #step>{{ step }}</template>
    <template #title>
      <template v-if="hasLicence && selectedProduct">
        Du har allerede tilgang til Kunne {{ selectedProduct.name }}
      </template>
      <template v-else-if="!hasLicence">
        {{ isPaid ? "Betaling" : "Prøv produktet" }}
      </template>
    </template>
    <template #content>
      <template v-if="!isAuthorized" />
      <template v-else-if="hasLicence">
        <UButtonNew
          :to="`/produkter/${
            productKey + '/' + selectedProduct?.variantKey
          }`"
          pill
          theme="cta"
          variant="filled"
        >
          Gå til Kunne {{ selectedProduct?.name ?? "" }}
        </UButtonNew>
      </template>
      <template v-else>
        <form class="flex text-xl" @submit.prevent="submit()">
          <section class="flex flex-col gap-12">
            <div v-if="isPaid" class="font-national2">
              <label class="flex items-baseline gap-3">
                <input
                  v-model="checkout.termsAccepted.value"
                  type="checkbox"
                  required
                />
                <span>
                  Jeg samtykker til
                  <a
                    href="/om/kjoepsvilkaar?side=3300"
                    style="font: inherit; text-decoration: underline"
                    >kjøpsvilkårene</a
                  >
                </span>
              </label>
            </div>

            <UCheckoutCard
              v-if="selectedProduct"
              :price="productPrice"
              :selected-product="selectedProduct"
              :show-payment="true"
            />
          </section>
        </form>
      </template>
    </template>
  </UStep>
  <UStep v-else :is-active-step="activeStep === step">
    <template #step>{{ step }}</template>
    <template #title>Betaling</template>
  </UStep>
</template>

<style scoped lang="postcss"></style>
